import { useContext, useMemo } from "react";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useWorkspaceAccounts } from "@api/account.api";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { useGLDrillDownUrl } from "@components/accounting/useGLDrillDownUrl";
import { getMultiEntityBasisInfo } from "@components/accounting/generalLedger/multiEntityLedger";
import { DirectoryContext } from "./Directory.context";

/**
 * Builds the multi-entity General Ledger drill-down URL for a counterparty's
 * journal entry lines, carrying over the directory's date range, ledger basis
 * (cash/accrual), entity selection and chart-of-accounts filter.
 */
export function useDirectoryGlDrillDownUrl(
    counterpartyId: string | null,
    isNoCounterparty: boolean,
): string {
    const { filters, view } = useContext(DirectoryContext);
    const businessEntityIds = useBusinessNonMockEntityIds();
    const { data: accounts } = useWorkspaceAccounts();
    const { data: ledgersByEntityId } =
        useLedgersByEntityIds(businessEntityIds);

    const coaAccounts = view.filters.coaAccounts;

    const entityIds = useMemo(
        () =>
            filters.entityIds?.length ? filters.entityIds : businessEntityIds,
        [filters.entityIds, businessEntityIds],
    );

    const ledgerId = useMemo(() => {
        if (!ledgersByEntityId) {
            return undefined;
        }
        const basisInfo = getMultiEntityBasisInfo({
            ledgersByEntityId,
            selectedEntityIds: entityIds,
        });
        return filters.basis === AccountingLedgerBasis.accruals
            ? basisInfo.accrualLedgerId
            : basisInfo.cashLedgerId;
    }, [ledgersByEntityId, entityIds, filters.basis]);

    const accountCodes = useMemo(() => {
        if (!coaAccounts?.length || !accounts) {
            return undefined;
        }
        const selectedIds = new Set(coaAccounts);
        const codes = accounts
            .filter((account) => selectedIds.has(account.id))
            .map((account) => account.code);
        return codes.length ? [...new Set(codes)] : undefined;
    }, [coaAccounts, accounts]);

    return useGLDrillDownUrl({
        counterpartiesIds: counterpartyId ? [counterpartyId] : undefined,
        withEmptyOrUnverifiedCounterparty: isNoCounterparty ? true : undefined,
        entityIds,
        ledgerId,
        accountCodes,
        journalEntrySources: view.filters.journalEntrySources,
        // Match the directory's effective period so the GL report shows the same
        // journal entry lines: a specific date range, or all time when none is set.
        ...(filters.startDate || filters.endDate
            ? {
                  startDate: filters.startDate,
                  endDate: filters.endDate,
              }
            : { isAllTime: true }),
    });
}
